// Page entry. PRD 18 §1.2:
//   - SYSTEM_MGMT:NO_PERMISSION (seeds OPERATION/CONTENT) → LNB hidden + entry
//     blocked (rendered as NoPermissionGate if a user navigates directly).
//   - READ / WRITE / Root → see the page.

import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';

import { toast } from '@/components/feedback/useToast';
import { useCan } from '@/features/roles/hooks/use-can';
import { useRequirePermission } from '@/shared/hooks/useRequirePermission';
import { LogInquiryFilters } from '@/features/log-inquiry/components/LogInquiryFilters';
import { LogInquiryListView } from '@/features/log-inquiry/components/LogInquiryListView';
import { useExportLogs } from '@/features/log-inquiry/hooks/use-log-mutations';
import { toQueryFilters } from '@/features/log-inquiry/hooks/use-logs';
import { useLogInquiryListStore } from '@/features/log-inquiry/store/log-inquiry-list.store';
import { LogInquiryApiError } from '@/features/log-inquiry/types/log.types';

export function LogInquiryPage() {
  const { t } = useTranslation('admin');
  // PRD §1.2 — the list needs READ or higher. A visitor without it gets the shared Korean
  // access-denied notice and is sent home, exactly like every other gated admin screen
  // (약관관리, 환자관리, …). Before SHCT-444 this page returned null instead, which showed
  // a blank white page and told the user nothing.
  const canRead = useRequirePermission('SYSTEM_MGMT', 'READ');
  // FEATURE_062 — export needs WRITE (or Root). A READ-only viewer still SEES the button,
  // greyed out with the reason on hover (SHCT-444), rather than not seeing it at all: a
  // missing control tells the user nothing, a disabled one tells them the feature exists and
  // that they lack the permission. The screen is never the only guard — the server rejects a
  // forged request with EXPORT_NOT_PERMITTED regardless.
  const canExport = useCan('SYSTEM_MGMT', 'WRITE');
  const filters = useLogInquiryListStore((s) => s.filters);
  const exportLogs = useExportLogs();

  if (!canRead) return null;

  const handleExport = () => {
    exportLogs.mutate(toQueryFilters(filters), {
      onSuccess: () => toast.success(t('logInquiry.toasts.downloadSuccess')),
      onError: (e) => {
        // Nothing matched the filters, so the server deliberately produced no file
        // (SHCT-441). That is information for the user, not a failure — say so
        // plainly and leave the screen as it is.
        if (e instanceof LogInquiryApiError && e.code === 'EXPORT_NO_DATA') {
          toast.info(t('logInquiry.toasts.downloadEmpty'));
          return;
        }
        // 413 (50k cap) carries the server's Korean message; show it verbatim. A 403
        // INSUFFICIENT_PERMISSION arrives already rewritten to the §7.4 P-4 string by the
        // response interceptor (SHCT-582), so this line prints that, not `권한이 없습니다.`.
        toast.error(
          e instanceof LogInquiryApiError ? e.message : t('logInquiry.toasts.downloadError'),
        );
      },
    });
  };

  return (
    <>
      <div className="content__header">
        <div>
          <h1 className="content__title">{t('logInquiry.title')}</h1>
        </div>
        <button
          type="button"
          className="btn btn-default btn-outline"
          onClick={handleExport}
          disabled={!canExport || exportLogs.isPending}
          title={canExport ? undefined : t('logInquiry.actions.downloadCsvNoPermission')}
        >
          <Download size={16} />
          {exportLogs.isPending
            ? t('logInquiry.actions.downloadCsvLoading')
            : t('logInquiry.actions.downloadCsv')}
        </button>
      </div>
      <div className="content__body">
        <LogInquiryFilters />
        <LogInquiryListView />
      </div>
    </>
  );
}